import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigationType } from "react-router-dom";
import { localizeArt } from './../localize.js';
import MyUserContext from './../myUserContext.js';
import PlatformRatings from './PlatformRatings';
import ArtThumbnail from './ArtThumbnail';
import { Text } from '@mantine/core';
import { RatingPillsStack } from './ratingPills';

const CARD_WIDTH_PX = 160;
const GAP_PX = 16;

const CATEGORY_ENUM = {
  1: "movie",
  2: "videoGame",
  3: "book"
};

const DEFAULT_CATEGORIES = ["movie", "book", "videoGame"];

const ImageScroller = ({
  arts,
  categories = DEFAULT_CATEGORIES,
  showCategoryTitles = true,
  aiReasonedArts = [],
  basedOnRecentLogbookArts = [],
  // Optional stable key (e.g. the host art's id). When provided, the
  // per-category "Показать больше" expansion is persisted to
  // sessionStorage so a back-navigation restores the expanded strips.
  // Required for ScrollRestoration to find a DOM tall enough to reach
  // the previous scroll position.
  scrollKey = null,
}) => {
  const myUser = useContext(MyUserContext);
  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const navigationType = useNavigationType();
  const containerRefs = useRef({});
  const [colsByCat, setColsByCat] = useState({});
  // Expansion persistence rule:
  // - Fresh PUSH/REPLACE to an art -> start collapsed (ignore any cache).
  // - POP back to an art -> hydrate expansion from cache.
  //
  // ArtPage mounts <ImageScroller key={art.id} />, so this component gets
  // a FRESH mount on every art change — the lazy useState initializer is
  // the only hook we need. No load useEffect: an effect gated on
  // [scrollKey, navigationType] fires on an intermediate re-render too
  // (navigationType flips POP->PUSH before art.id has updated, so the
  // still-mounted previous ImageScroller fires with its OLD scrollKey +
  // the NEW nav and wipes its own cache). The initializer sees only the
  // committed values at mount, which are the right ones.
  //
  // Writes to cache happen directly in showMore (the only state change
  // worth persisting).
  const [visibleByCat, setVisibleByCat] = useState(() => {
    if (!scrollKey || navigationType !== 'POP') return {};
    try {
      const cached = sessionStorage.getItem(`imgscroller-visible:${scrollKey}`);
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });

  const measureCols = () => {
    const next = {};
    categories.forEach((cat) => {
      const el = containerRefs.current[cat];
      if (!el) return;
      const w = el.clientWidth;
      if (!w) return;
      next[cat] = Math.max(1, Math.floor((w + GAP_PX) / (CARD_WIDTH_PX + GAP_PX)));
    });
    setColsByCat(next);
    setVisibleByCat((prev) => {
      let changed = false;
      const out = { ...prev };
      categories.forEach((cat) => {
        if (out[cat] === undefined && next[cat]) {
          out[cat] = next[cat];
          changed = true;
        }
      });
      return changed ? out : prev;
    });
  };

  useLayoutEffect(() => {
    measureCols();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arts, categories]);

  useEffect(() => {
    const onResize = () => measureCols();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arts, categories]);

  const showMore = (cat) => {
    const cols = colsByCat[cat] || 1;
    setVisibleByCat((prev) => {
      const next = { ...prev, [cat]: (prev[cat] || cols) + cols * 2 };
      if (scrollKey) {
        try { sessionStorage.setItem(`imgscroller-visible:${scrollKey}`, JSON.stringify(next)); } catch {}
      }
      return next;
    });
  };

  const categoryLabel = (cat) => {
    if (cat === "movie") return language === "ru" ? "Фильмы" : "Movies";
    if (cat === "book") return language === "ru" ? "Книги" : "Books";
    return language === "ru" ? "Игры" : "Video Games";
  };

  const genitiveMoreLabel = (cat, remaining) => {
    if (language === "ru") {
      const noun = cat === "movie" ? "фильмов" : cat === "book" ? "книг" : "игр";
      return `Ещё ${remaining} ${noun}`;
    }
    const noun = cat === "movie" ? "movies" : cat === "book" ? "books" : "games";
    return `Show ${remaining} more ${noun}`;
  };

  return (
    <div className="space-y-10">
      {categories.map((cat) => {
        const filteredArts = arts.filter(
          (item) => CATEGORY_ENUM[item.category] === cat
        );

        if (filteredArts.length === 0) return null;

        const cols = colsByCat[cat] || 1;
        const visible = visibleByCat[cat] ?? cols;
        const shownArts = filteredArts.slice(0, visible);
        const hasMore = visible < filteredArts.length;
        const remaining = filteredArts.length - visible;

        return (
          <div key={cat} className="space-y-4">
            {/* Section header: bold, left-aligned on wide screens, with
                a count badge. Treats each category as a real section,
                not an afterthought label. */}
            {showCategoryTitles && (
              <div className="flex items-center justify-center gap-2 px-1">
                <span className="font-semibold text-base" style={{ color: 'var(--mantine-color-text)' }}>
                  {categoryLabel(cat)}
                </span>
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: 'var(--mantine-color-default)', color: 'var(--mantine-color-dimmed)' }}
                >
                  {filteredArts.length}
                </span>
              </div>
            )}

            <div
              ref={(el) => { containerRefs.current[cat] = el; }}
              className="grid gap-4 justify-center"
              style={{ gridTemplateColumns: `repeat(auto-fit, ${CARD_WIDTH_PX}px)` }}
            >
              {shownArts.map((item, index) => {
                const localizedArt = localizeArt(item, language);
                return (
                  <div key={index + item.id}>
                    <Link to={`/arts/${item.id}`} className="group block w-40">
                      {/* Poster + overlay badges. Outer relative so the
                          badge can position-absolute without being
                          clipped by the inner rounded-xl overflow. */}
                      <div className="relative">
                        <div
                          className="rounded-xl overflow-hidden shadow-sm transition-transform duration-200 group-hover:-translate-y-1 group-hover:shadow-lg"
                          style={{ backgroundColor: 'var(--mantine-color-body)' }}
                        >
                          <ArtThumbnail art={item} />
                        </div>
                        <RatingPillsStack art={item} max={3} />
                      </div>
                      <div className="mt-2 text-center px-1">
                        <Text size="xs" fw={500} lineClamp={1} lh="sm">
                          {localizedArt.name}
                        </Text>
                        {item.year ? (
                          <Text size="xs" c="dimmed" lh="xs" mt={2}>
                            {item.year}
                          </Text>
                        ) : null}
                      </div>
                    </Link>
                    {/* Keep the dense PlatformRatings row underneath for
                        users who want multi-platform detail. The corner
                        badge is the at-a-glance signal; this is the
                        reference. */}
                    <div className="mt-1.5 flex justify-center w-40">
                      <PlatformRatings art={item} center={true} />
                    </div>
                    {aiReasonedArts.includes(item.id) && (
                      <div className="flex justify-center mt-1">
                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'var(--mantine-color-blue-light)', color: 'var(--mantine-color-blue-filled)' }}>
                          {language === "ru" ? "ИИ" : "AI"}
                        </span>
                      </div>
                    )}
                    {basedOnRecentLogbookArts.includes(item.id) && (
                      <div className="flex justify-center mt-1">
                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'var(--mantine-color-yellow-light)', color: 'var(--mantine-color-yellow-filled)' }}>
                          {language === "ru" ? "Из журнала" : "Logbook"}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {hasMore && (
              // Full-width ghost button with the exact remaining count
              // and a chevron. Removes the ambiguity of a bare 'больше'.
              <div className="flex justify-center px-2">
                <button
                  type="button"
                  onClick={() => showMore(cat)}
                  className="w-full max-w-md flex items-center justify-center gap-1 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    border: '1px solid var(--mantine-color-default-border)',
                    background: 'transparent',
                    color: 'var(--mantine-color-text)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{genitiveMoreLabel(cat, remaining)}</span>
                  <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>▾</span>
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ImageScroller;
